import React from "react";
import { motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { buildWhatsAppLink, SITE } from "@/lib/site";

const links = [
  { label: "Philosophy", href: "#philosophy" },
  { label: "Journey", href: "#journey" },
  { label: "Treatments", href: "#treatments" },
  { label: "Food", href: "#nourishment" },
  { label: "Villa", href: "#villa" },
  { label: "Pricing", href: "#pricing" },
  { label: "FAQ", href: "#faq" },
];

export const Navbar = () => {
  const [open, setOpen] = React.useState(false);

  return (
    <motion.header
      initial={{ y: -16, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 inset-x-0 z-40 backdrop-blur-xl bg-ivory/80 border-b border-sage/10"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 h-24 md:h-28 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-2.5 group" data-testid="nav-logo">
          <img
            src={SITE.logoLight}
            alt="Kakkanat Ayurveda"
            className="h-14 md:h-16 lg:h-[72px] w-auto object-contain"
            style={{ mixBlendMode: "multiply" }}
            draggable={false}
          />
        </a>

        <nav className="hidden md:flex items-center gap-10" data-testid="nav-links">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-sm font-medium text-charcoal/70 hover:text-charcoal transition-colors"
              data-testid={`nav-link-${l.label.toLowerCase()}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href="tel:+919061622255"
            aria-label="Call Kakkanat"
            className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-sage text-ivory hover:bg-sage-700 transition-colors"
            data-testid="nav-call-button"
          >
            <Phone size={15} strokeWidth={1.75} />
          </a>
          <a
            href={buildWhatsAppLink("availability")}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Chat on WhatsApp"
            className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-sage text-ivory hover:bg-sage-700 transition-colors"
            data-testid="nav-cta-button"
          >
            <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.198-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479c0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.076 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.247-.694.247-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.04 21.785h-.005a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.981.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.002-5.45 4.436-9.884 9.889-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885zm8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.892a11.821 11.821 0 0 0-3.48-8.413z"/>
            </svg>
          </a>
        </div>

        <div className="flex items-center gap-2 md:hidden">
          <a
            href="tel:+919061622255"
            aria-label="Call Kakkanat"
            className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-sage text-ivory hover:bg-sage-700 transition-colors"
            data-testid="nav-mobile-call"
          >
            <Phone size={16} strokeWidth={1.75} />
          </a>
          <button
            aria-label="Open menu"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-sage/30 text-sage"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="md:hidden border-t border-sage/10 bg-ivory/95"
          data-testid="nav-mobile-panel"
        >
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-base text-charcoal/80 hover:text-charcoal"
                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
              >
                {l.label}
              </a>
            ))}
            <a
              href="tel:+919061622255"
              className="inline-flex items-center justify-center gap-2 border border-sage/40 text-charcoal px-5 py-3 rounded-full text-sm font-medium"
              data-testid="nav-mobile-call-link"
            >
              <Phone size={15} strokeWidth={1.75} />
              Call +91 90616 22255
            </a>
            <a
              href={buildWhatsAppLink("availability")}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center justify-center bg-sage text-ivory px-5 py-3 rounded-full text-sm font-medium"
              data-testid="nav-mobile-cta"
            >
              Check Availability
            </a>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
};

export default Navbar;
